import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { format } from "date-fns";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loading } from "@/components/ui/loading";
import { useToast } from "@/hooks/use-toast";
import { axiosInstance } from "@/pages/auth/auth.context";
import { EventStatusEnum, statusLabels } from "@/types/eventStatus";

/** The word typed to unlock the delete button, as GitHub does for repositories. */
export const CONFIRMATION_WORD = "confirm";

interface DeletionPreview {
  clientId: string;
  companyName: string | null;
  contactName: string | null;
  email: string | null;
  hasStripeCustomer: boolean;
  deletable: boolean;
  blockers: string[];
  events: {
    id: string;
    startTime: string | null;
    city: string | null;
    nameOfLocation: string | null;
    bidStatus: EventStatusEnum;
  }[];
  contactCount: number;
  journalEntryCount: number;
  queuedNotificationCount: number;
}

const plural = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

/**
 * Permanently deletes a client and every bid under it. Built for clearing out
 * spam bid requests.
 *
 * The backend refuses any client with an invoice, a check batch, or staff signed
 * up or waitlisted, and the preview says so before anything is typed. For a
 * client it will delete, the button stays disabled until "confirm" is typed.
 */
export function DeleteClientDialog({ clientId }: { clientId: string }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [refusal, setRefusal] = useState<string[] | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  const {
    data: preview,
    isLoading,
    isError,
  } = useQuery<DeletionPreview>({
    queryKey: ["client-deletion-preview", clientId],
    queryFn: async () =>
      (await axiosInstance.get(`client/${clientId}/deletion-preview`)).data,
    enabled: open,
    staleTime: 0,
  });

  const confirmed = typed.trim().toLowerCase() === CONFIRMATION_WORD;
  const blockers = refusal ?? preview?.blockers ?? [];
  const deletable = Boolean(preview?.deletable) && refusal === null;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setTyped("");
      setRefusal(null);
    }
  };

  const handleDelete = async () => {
    if (!confirmed) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`client/${clientId}`);
      toast({
        title: "Client deleted",
        description: `${preview?.companyName ?? "The client"} and its bids were permanently deleted.`,
        variant: "success",
      });
      setOpen(false);
      navigate("/admin/clients");
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        // Something changed since the preview, such as an invoice being sent.
        setRefusal(
          error.response.data?.blockers ?? [error.response.data?.error],
        );
      } else {
        toast({
          title: "Error",
          description: "The client could not be deleted. Nothing was removed.",
          variant: "destructive",
        });
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>
        <Button variant="destructive">Delete client</Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this client permanently?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-muted-foreground">
              {isLoading && <Loading />}

              {isError && (
                <p>
                  The client's details could not be loaded. Nothing will be
                  deleted.
                </p>
              )}

              {preview && !deletable && (
                <div className="space-y-2">
                  <p className="font-medium text-destructive">
                    This client can't be deleted, because it has real business
                    on record:
                  </p>
                  <ul className="list-disc pl-5">
                    {blockers.map((blocker) => (
                      <li key={blocker}>{blocker}</li>
                    ))}
                  </ul>
                </div>
              )}

              {preview && deletable && (
                <>
                  <p>
                    This permanently deletes{" "}
                    <span className="font-medium text-foreground">
                      {preview.companyName ||
                        preview.contactName ||
                        "this client"}
                    </span>
                    {preview.email ? ` (${preview.email})` : ""},{" "}
                    {plural(preview.events.length, "bid", "bids")},{" "}
                    {plural(preview.contactCount, "contact", "contacts")},{" "}
                    {plural(
                      preview.journalEntryCount,
                      "journal note",
                      "journal notes",
                    )}{" "}
                    and{" "}
                    {plural(
                      preview.queuedNotificationCount,
                      "email record",
                      "email records",
                    )}
                    {preview.hasStripeCustomer
                      ? ", and its Stripe customer"
                      : ""}
                    .
                  </p>
                  {preview.events.length > 0 && (
                    <ul className="list-disc pl-5">
                      {preview.events.map((event) => (
                        <li key={event.id}>
                          {event.startTime
                            ? format(new Date(event.startTime), "MMM d, yyyy")
                            : "No date"}
                          {event.nameOfLocation || event.city
                            ? ` · ${event.nameOfLocation || event.city}`
                            : ""}
                          {" · "}
                          {statusLabels[event.bidStatus] ?? event.bidStatus}
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="font-medium text-destructive">
                    This cannot be undone.
                  </p>
                  <div className="space-y-2">
                    <Label
                      htmlFor="delete-client-confirmation"
                      className="text-foreground"
                    >
                      Type{" "}
                      <span className="font-mono font-semibold">
                        {CONFIRMATION_WORD}
                      </span>{" "}
                      to continue
                    </Label>
                    <Input
                      id="delete-client-confirmation"
                      value={typed}
                      onChange={(e) => setTyped(e.target.value)}
                      autoComplete="off"
                      disabled={deleting}
                    />
                  </div>
                </>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
          {preview && deletable && (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={!confirmed || deleting}
            >
              {deleting ? "Deleting..." : "Delete this client"}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
